import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCcw,
  Search,
  UserRound,
} from "lucide-react";
import { adminService } from "../../services/adminService";

const TABS = [
  { key: "needs_action", label: "Needs Action" },
  { key: "upcoming", label: "Upcoming" },
  { key: "awaiting_agenda", label: "Awaiting Note" },
  { key: "completed", label: "Completed" },
  { key: "all", label: "All" },
];

const STATUS_OPTIONS = [
  ["", "All statuses"],
  ["pending_payment", "Pending payment"],
  ["paid", "Paid"],
  ["paid_needs_reschedule", "Needs reschedule"],
  ["confirmed", "Confirmed"],
];

const AccountabilityBookings = () => {
  const [activeTab, setActiveTab] = useState("needs_action");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(() => {
    const next = { limit: 200 };
    if (status) next.status = status;
    if (search.trim()) next.search = search.trim();
    if (activeTab === "needs_action") next.needs_action = true;
    if (activeTab === "awaiting_agenda") {
      next.status = "confirmed";
      next.has_agenda = false;
    }
    if (activeTab === "completed") next.has_agenda = true;
    return next;
  }, [activeTab, search, status]);

  const visibleItems = useMemo(() => {
    const now = Date.now();
    if (activeTab !== "upcoming") return items;
    return items.filter((item) => {
      const slot = item.slot_start ? Date.parse(item.slot_start) : null;
      return slot && slot >= now;
    });
  }, [activeTab, items]);

  const loadBookings = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminService.getAccountabilityBookings(params);
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setSummary(payload.summary || {});
      if (selected) {
        const refreshed = (payload.items || []).find(
          (item) => item.booking_id === selected.booking_id
        );
        if (refreshed) setSelected(refreshed);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load accountability bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openDetail = async (booking) => {
    setSelected(booking);
    setDetailLoading(true);
    try {
      const detail = await adminService.getAccountabilityBookingDetail(
        booking.booking_id
      );
      setSelected(detail);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-display">Accountability Bookings</h1>
          <p className="text-sm text-muted">
            Track paid calls, user notes, AP assignment, and progression state.
          </p>
        </div>
        <button
          type="button"
          onClick={loadBookings}
          className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-title shadow-sm hover:bg-gray-50"
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-5">
        <MetricCard label="Loaded" value={summary.total ?? visibleItems.length} />
        <MetricCard label="Needs action" value={summary.needs_action ?? 0} tone="warning" />
        <MetricCard label="Awaiting note" value={summary.awaiting_agenda ?? 0} />
        <MetricCard label="Confirmed" value={summary.confirmed ?? 0} />
        <MetricCard label="Completed" value={summary.completed ?? 0} tone="success" />
      </div>

      <div className="rounded-lg bg-white p-4 shadow stakeholder-summary-card">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-md px-3 py-2 text-sm transition ${
                  activeTab === tab.key
                    ? "bg-[#54B0AF] text-white shadow-sm"
                    : "bg-[#F5F9F9] text-title hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search user, course, module..."
                className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm md:w-72"
              />
            </div>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="rounded-md border border-gray-200 px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map(([value, label]) => (
                <option key={value || "all"} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="overflow-hidden rounded-lg bg-white shadow stakeholder-summary-card">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-[#54B0AF]">
              <Loader2 size={20} className="animate-spin" />
              Loading bookings
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted">
              No accountability bookings match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <Th>User</Th>
                    <Th>When</Th>
                    <Th>Module</Th>
                    <Th>AP</Th>
                    <Th>Status</Th>
                    <Th>Note</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => (
                    <tr
                      key={item.booking_id}
                      onClick={() => openDetail(item)}
                      className={`cursor-pointer border-t hover:bg-[#F5F9F9] ${
                        selected?.booking_id === item.booking_id ? "bg-[#F5F9F9]" : ""
                      }`}
                    >
                      <Td>
                        <div className="font-semibold text-title">{item.user?.name || "--"}</div>
                        <div className="text-xs text-muted">{item.user?.email || "--"}</div>
                      </Td>
                      <Td>
                        <div className="font-medium text-title">{formatDateTime(item.slot_start)}</div>
                        <div className="text-xs text-muted">{item.timezone || ""}</div>
                      </Td>
                      <Td>
                        <div className="font-medium text-title">{item.module?.title || "--"}</div>
                        <div className="text-xs text-muted">{item.subsection?.title || "--"}</div>
                      </Td>
                      <Td>{item.accountability_partner?.name || <span className="text-muted">Unassigned</span>}</Td>
                      <Td>
                        <StatusBadge item={item} />
                      </Td>
                      <Td>
                        {item.discussion_agenda ? (
                          <CheckCircle2 size={17} className="text-[#54B0AF]" />
                        ) : (
                          <span className="text-xs text-muted">Missing</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <BookingDetail booking={selected} loading={detailLoading} />
      </div>
    </div>
  );
};

export default AccountabilityBookings;

const BookingDetail = ({ booking, loading }) => {
  if (!booking) {
    return (
      <div className="rounded-lg bg-white p-6 shadow stakeholder-summary-card">
        <div className="flex items-center gap-2 text-sm text-muted">
          <FileText size={17} />
          Select a booking to view details.
        </div>
      </div>
    );
  }

  return (
    <aside className="rounded-lg bg-white p-5 shadow stakeholder-summary-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted">
            <CalendarClock size={15} />
            Booking detail
          </div>
          <h2 className="mt-1 text-title font-semibold">{booking.user?.name || "--"}</h2>
          <p className="text-xs text-muted">{booking.booking_id}</p>
        </div>
        {loading && <Loader2 size={18} className="animate-spin text-[#54B0AF]" />}
      </div>

      <div className="space-y-4">
        <DetailSection title="Call">
          <DetailLine label="Slot" value={formatDateTimeRange(booking.slot_start, booking.slot_end)} />
          <DetailLine label="Status" value={<StatusBadge item={booking} />} />
          <DetailLine label="Payment" value={booking.payment?.status || "--"} />
          <DetailLine label="Paid at" value={formatDateTime(booking.paid_at || booking.payment?.paid_at)} />
          <DetailLine label="Confirmed at" value={formatDateTime(booking.confirmed_at)} />
          {booking.calendar_link && (
            <a
              href={booking.calendar_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#007D7A] underline"
            >
              Open calendar event <ExternalLink size={14} />
            </a>
          )}
        </DetailSection>

        <DetailSection title="User Note">
          {booking.discussion_agenda ? (
            <p className="whitespace-pre-wrap rounded-md bg-[#F5F9F9] p-3 text-sm text-title">
              {booking.discussion_agenda}
            </p>
          ) : (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-700">
              <AlertCircle size={16} />
              User has not submitted what they want to discuss.
            </div>
          )}
        </DetailSection>

        <DetailSection title="Progress">
          <DetailLine label="Course" value={booking.course?.title || "--"} />
          <DetailLine label="Module" value={booking.module?.title || "--"} />
          <DetailLine label="Subsection" value={booking.subsection?.title || "--"} />
          <DetailLine label="Progress status" value={formatLabel(booking.progress?.status)} />
          <DetailLine label="Completed at" value={formatDateTime(booking.progress?.completed_at)} />
          <div className="flex flex-wrap gap-2 pt-1">
            {booking.user?.user_id && (
              <Link
                to={`/admin/users/${booking.user.user_id}/progress-summary`}
                className="inline-flex items-center gap-1 rounded-md bg-[#54B0AF] px-3 py-2 text-xs font-semibold text-white"
              >
                <UserRound size={14} />
                User progress
              </Link>
            )}
            {booking.course?.course_id && (
              <Link
                to={`/admin/courses/${booking.course.course_id}`}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-xs font-semibold text-title"
              >
                Course detail
              </Link>
            )}
          </div>
        </DetailSection>

        <DetailSection title="Accountability Partner">
          <DetailLine label="Assigned AP" value={booking.accountability_partner?.name || "Unassigned"} />
          <DetailLine label="AP email" value={booking.accountability_partner?.email || "--"} />
          <DetailLine label="Assigned at" value={formatDateTime(booking.accountability_partner?.assigned_at)} />
        </DetailSection>
      </div>
    </aside>
  );
};

const MetricCard = ({ label, value, tone }) => {
  const toneClass =
    tone === "warning"
      ? "text-amber-700"
      : tone === "success"
        ? "text-[#007D7A]"
        : "text-title";
  return (
    <div className="rounded-lg bg-white p-4 shadow stakeholder-summary-card">
      <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <section className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
    <h3 className="mb-3 text-sm font-semibold text-title">{title}</h3>
    <div className="space-y-2">{children}</div>
  </section>
);

const DetailLine = ({ label, value }) => (
  <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 text-sm">
    <div className="text-muted">{label}</div>
    <div className="break-words text-title">{value || "--"}</div>
  </div>
);

const StatusBadge = ({ item }) => {
  const tone = item.needs_action
    ? "bg-amber-50 text-amber-700 border-amber-100"
    : item.progress?.status === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : "bg-[#F5F9F9] text-[#007D7A] border-[#CFECEC]";
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {item.needs_action ? "Needs action" : formatLabel(item.status)}
    </span>
  );
};

const Th = ({ children }) => (
  <th className="px-4 py-3 font-semibold">{children}</th>
);

const Td = ({ children }) => (
  <td className="px-4 py-3 align-top">{children}</td>
);

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTimeRange = (start, end) => {
  if (!start) return "--";
  const startLabel = formatDateTime(start);
  const endDate = end ? new Date(end) : null;
  if (!endDate || Number.isNaN(endDate.getTime())) return startLabel;
  return `${startLabel} - ${endDate.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const formatLabel = (value) => {
  if (!value) return "--";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
